import React, { useEffect, useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { ShrinkableTextValue } from 'src/components/common/ShrinkableTextValue';
import { REGIONS } from 'src/constants';
import {
  PermissionScopeBusinessLines as ALL_BUSINESS_LINES,
  PermissionScopePublishers as ALL_PUBLISHERS,
} from 'src/store/constants';
import type { Country } from 'src/store/generated/leela-api-generated';
import {
  useBusinessUnitAdminControllerUpdateGroupMutation,
  useBusinessUnitAdminControllerUpdateGroupStatusMutation,
} from 'src/store/leela-api';
import { GroupRoleLabelKeys, isExternalGroupRole } from 'src/utils/groupRoles';
import type { GroupRole } from 'src/utils/groupRoles';

interface EditGroupModalProps {
  group: {
    id: string;
    name: string;
    role: GroupRole;
    countries: string[];
    publishers: string[];
    businessLines: string[];
    status: string;
    partnerId?: string;
    dataVisibilityPolicy?: string;
  };
  businessUnitCountries: string[];
  open: boolean;
  partnerName?: string;
  onClose: () => void;
}

const SELECT_ALL = '__SELECT_ALL__';
const DESELECT_ALL = '__DESELECT_ALL__';

export const EditGroupModal: React.FC<EditGroupModalProps> = ({
  group,
  businessUnitCountries,
  open,
  partnerName,
  onClose,
}) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [updateGroup, { isLoading: isSaving }] = useBusinessUnitAdminControllerUpdateGroupMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] = useBusinessUnitAdminControllerUpdateGroupStatusMutation();

  const [countries, setCountries] = useState<string[]>([]);
  const [publishers, setPublishers] = useState<string[]>([]);
  const [businessLines, setBusinessLines] = useState<string[]>([]);

  const isExternal = isExternalGroupRole(group.role);
  const isSubmitting = isSaving || isUpdatingStatus;

  useEffect(() => {
    setCountries([...group.countries]);
    setPublishers([...group.publishers]);
    setBusinessLines([...group.businessLines]);
    // The current group's draft scope must survive parent renders with equivalent new object references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, open]);

  const handleSave = async () => {
    try {
      await updateGroup({
        id: group.id,
        updateGroupDto: {
          countries: countries as Country[],
          publishers: publishers as Array<'meta' | 'tiktok' | 'google' | 'adsp'>,
          businessLines: businessLines as Array<'SVOD' | 'TVOD' | 'CHANNEL' | 'Sports SVOD' | 'Sports TVOD' | 'Sports Channel'>,
          dataVisibilityPolicy: (isExternal ? 'AGENCY_RESTRICTED' : 'FULL') as 'FULL' | 'DIRECTIONAL' | 'AGENCY_RESTRICTED',
        },
      }).unwrap();
      createToast({ type: AlertType.SUCCESS, message: t('edit_group_update_success') });
      onClose();
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data?.message;
      createToast({ type: AlertType.ERROR, message: apiMessage ?? t('edit_group_update_error') });
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateStatus({
        id: group.id,
        updateGroupStatusDto: { status: newStatus as 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' },
      }).unwrap();
      createToast({ type: AlertType.SUCCESS, message: t('edit_group_status_update_success', { status: newStatus }) });
      onClose();
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data?.message;
      createToast({ type: AlertType.ERROR, message: apiMessage ?? t('edit_group_status_update_error') });
    }
  };

  const handleCountryChange = (value: string | string[]) => {
    const values = Array.isArray(value) ? value : [value];
    if (values.includes(SELECT_ALL)) {
      setCountries([...businessUnitCountries]);
    } else if (values.includes(DESELECT_ALL)) {
      setCountries([]);
    } else {
      const regionValues = values.filter(candidate => candidate.startsWith('REGION:'));
      const countryValues = values.filter(candidate => !candidate.startsWith('REGION:'));
      const nextCountries = new Set(countryValues);

      regionValues.forEach(regionValue => {
        const regionName = regionValue.replace('REGION:', '');
        const region = REGIONS.find(candidate => candidate.name === regionName);
        const regionCountries = (region?.countries ?? [])
          .filter(country => businessUnitCountries.includes(country as string)) as string[];
        const isRegionSelected = regionCountries.every(country => countryValues.includes(country));

        regionCountries.forEach(country => {
          if (isRegionSelected) {
            nextCountries.delete(country);
          } else {
            nextCountries.add(country);
          }
        });
      });
      setCountries(Array.from(nextCountries));
    }
  };

  return (
    <Modal
      data-testid="edit-group-modal"
      open={open}
      scrollContainer="modal"
      title={t('edit_group_title')}
      width={640}
      onClose={onClose}
    >
      <Column spacing="400">
        <Row spacing="200">
          <Text color="secondary" type="b300">{t('edit_group_name_label')}</Text>
          <ShrinkableTextValue testId="edit-group-name-value" type="b300">
            {group.name}
          </ShrinkableTextValue>
        </Row>
        <Row spacing="200">
          <Text color="secondary" type="b300">{t('edit_group_role_label')}</Text>
          <Text type="b300">{GroupRoleLabelKeys[group.role] ? (t(GroupRoleLabelKeys[group.role]) ?? group.role) : group.role}</Text>
          {isExternal && <Tag type="warning">{t('edit_group_external')}</Tag>}
        </Row>
        <Row spacing="200">
          <Text color="secondary" type="b300">{t('edit_group_partner_label')}</Text>
          <ShrinkableTextValue testId="edit-group-partner-value" type="b300">
            {group.partnerId ? (partnerName ?? group.partnerId) : t('edit_group_internal')}
          </ShrinkableTextValue>
        </Row>
        <Row spacing="200">
          <Text color="secondary" type="b300">{t('edit_group_status_label')}</Text>
          <Tag type={group.status === 'ACTIVE' ? 'success' : group.status === 'INACTIVE' ? 'warning' : 'neutral'}>{group.status}</Tag>
        </Row>

        <Divider />

        <Text color="secondary" data-testid="edit-group-scope-help" type="b200">
          {t('create_group_scope_help')}
        </Text>

        <Column spacing="100">
          <Text type="b300">{t('edit_group_countries_label')}</Text>
          <Select
            data-testid="edit-group-countries"
            placeholder={t('create_group_countries_placeholder')}
            value={countries}
            onChange={handleCountryChange}
          >
            <SelectOption
              label={countries.length === businessUnitCountries.length ? t('edit_group_deselect_all') : t('edit_group_select_all')}
              value={countries.length === businessUnitCountries.length ? DESELECT_ALL : SELECT_ALL}
            />
            {REGIONS
              .filter(region => region.countries.some(country => businessUnitCountries.includes(country as string)))
              .map(region => {
                const regionCountries = region.countries.filter(country => businessUnitCountries.includes(country as string));
                return [
                  <SelectOption key={`region-${region.name}`} label={`── ${region.name} (${regionCountries.length}) ──`} value={`REGION:${region.name}`} />,
                  ...regionCountries.map(country => (
                    <SelectOption key={country} label={`  ${country}`} value={country as string} />
                  )),
                ];
              })
              .flat()}
          </Select>
        </Column>

        <Column spacing="100">
          <Text type="b300">{t('edit_group_publishers_label')}</Text>
          <Select
            data-testid="edit-group-publishers"
            placeholder={t('edit_group_publishers_placeholder')}
            value={publishers}
            onChange={(value: string | string[]) => {
              const values = Array.isArray(value) ? value : [value];
              if (values.includes(SELECT_ALL)) {
                setPublishers([...ALL_PUBLISHERS]);
              } else if (values.includes(DESELECT_ALL)) {
                setPublishers([]);
              } else {
                setPublishers(values);
              }
            }}
          >
            <SelectOption
              label={publishers.length === ALL_PUBLISHERS.length ? t('edit_group_deselect_all') : t('edit_group_select_all')}
              value={publishers.length === ALL_PUBLISHERS.length ? DESELECT_ALL : SELECT_ALL}
            />
            {ALL_PUBLISHERS.map(publisher => (
              <SelectOption key={publisher} label={publisher} value={publisher} />
            ))}
          </Select>
        </Column>

        <Column spacing="100">
          <Text type="b300">{t('edit_group_business_lines_label')}</Text>
          <Select
            data-testid="edit-group-business-lines"
            placeholder={t('edit_group_business_lines_placeholder')}
            value={businessLines}
            onChange={(value: string | string[]) => {
              const values = Array.isArray(value) ? value : [value];
              if (values.includes(SELECT_ALL)) {
                setBusinessLines([...ALL_BUSINESS_LINES]);
              } else if (values.includes(DESELECT_ALL)) {
                setBusinessLines([]);
              } else {
                setBusinessLines(values);
              }
            }}
          >
            <SelectOption
              label={businessLines.length === ALL_BUSINESS_LINES.length ? t('edit_group_deselect_all') : t('edit_group_select_all')}
              value={businessLines.length === ALL_BUSINESS_LINES.length ? DESELECT_ALL : SELECT_ALL}
            />
            {ALL_BUSINESS_LINES.map(businessLine => (
              <SelectOption key={businessLine} label={businessLine} value={businessLine} />
            ))}
          </Select>
        </Column>
        <Divider />

        <Column spacing="100">
          <Text type="b300">{t('edit_group_status_actions_label')}</Text>
          <Row spacing="200">
            {group.status === 'ACTIVE' && (
              <Button data-testid="edit-group-deactivate" disabled={isSubmitting} type="secondary" onClick={() => handleStatusChange('INACTIVE')}>
                {t('edit_group_deactivate')}
              </Button>
            )}
            {group.status === 'INACTIVE' && (
              <>
                <Button data-testid="edit-group-activate" disabled={isSubmitting} type="secondary" onClick={() => handleStatusChange('ACTIVE')}>
                  {t('edit_group_reactivate')}
                </Button>
                <Button data-testid="edit-group-archive" disabled={isSubmitting} type="secondary" onClick={() => handleStatusChange('ARCHIVED')}>
                  {t('edit_group_archive')}
                </Button>
              </>
            )}
            {group.status === 'ARCHIVED' && (
              <Text color="secondary" type="b200">{t('edit_group_archived_help')}</Text>
            )}
          </Row>
        </Column>
      </Column>

      <div style={{ marginTop: '24px' }}>
        <ModalFooter>
          <Row alignmentHorizontal="end" spacing="300">
            <Button data-testid="edit-group-cancel" type="secondary" onClick={onClose}>
              {t('edit_group_cancel')}
            </Button>
            <Button
              data-testid="edit-group-save"
              disabled={isSubmitting || group.status === 'ARCHIVED'}
              type="primary"
              onClick={handleSave}
            >
              {t('edit_group_save_changes')}
            </Button>
          </Row>
        </ModalFooter>
      </div>
    </Modal>
  );
};
